import {
  CreateGroup,
  DistributionStatus,
  Event,
  Organizer,
  OrganizerType,
  PriorizationType,
  ResolvedEvent
} from '@thro-types/events';

import { isBefore } from 'date-fns';
import { Gateway } from 'index';
import { omitBy } from 'lodash';
import omitDeep from 'omit-deep-lodash';

import { S3Response } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';

export const isOrganizer = (
  currentUser,
  authorRoles,
  organizers: Organizer[] = [],
  canEdit = false,
  hostId = 0
): boolean => {
  const aclHelper = new ACLHelper(currentUser);
  if (aclHelper.isSuperUser()) {
    return true;
  }

  let response = false;
  organizers.forEach(o => {
    if (o.organizerType === OrganizerType.USER) {
      if (canEdit && authorRoles.find(role => parseInt(role.id, 10) === hostId)) {
        response = true;
        return;
      } else if (currentUser.id === o.refId) {
        response = true;
        return;
      }
    }
    if (o.organizerType === OrganizerType.ROLE) {
      authorRoles.forEach(r => {
        if (parseInt(r.id) === o.refId) {
          response = true;
          return;
        }
      });
    }
  });
  return response;
};

export const uploadAttachmentToS3 = async (attachments: File[]) => {
  const token = localStorage.getItem('jwt-token');
  const refreshToken = localStorage.getItem('refresh-token');
  const formData = new FormData();
  let response: { name: string; path: string }[] = [];
  attachments.forEach(a => {
    if (a instanceof Blob) {
      'name' in a &&
        typeof a.name === 'string' &&
        formData.append('files', a, encodeURIComponent(a.name));
    } else response.push(a);
  });
  const { data }: { data: S3Response[] } = await Gateway.post(
    '/events/attachments/upload',
    formData,
    {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        refresh: refreshToken || ''
      }
    }
  );
  response.push(
    ...data.map(attachment => {
      return { name: attachment.OriginalName, path: attachment.Key };
    })
  );
  return response;
};

export const isEventActive = (event: ResolvedEvent) => {
  const isCancelled = event.isCancelled;
  const hasEnded = event.endDate?.toString() < new Date().toISOString();

  return !(isCancelled || hasEnded);
};

export const isEnrollable = (event: ResolvedEvent) => {
  return event.registrationPeriodStart?.toString() < new Date().toISOString();
};

export const isRegistrationPeriodOver = (event: ResolvedEvent) => {
  return event.registrationPeriodEnd?.toString() < new Date().toISOString();
};

export const isPreCalc = (eventData: ResolvedEvent) => {
  return (
    eventData.distributionStatus === DistributionStatus.NOT_DISTRIBUTED &&
    eventData.priorizationType === PriorizationType.SEMESTER
  );
};

export function removeDuplicates<T>(array: T[], keyFunction: (item: T) => number): T[] {
  const seen = new Set();
  return array.filter(item => {
    const key = keyFunction(item);
    if (!seen.has(key)) {
      seen.add(key);
      return true;
    }
    return false;
  });
}

/**
 * Check if max participant field is invalid.
 *  - if event registration period has started and new group max participant is less than previous group max participant
 * @param index
 * @param previousEvent
 * @param groups
 * @returns
 */
export const isMaxParticipantFieldInvalid = (
  index: number,
  previousEvent?: Event,
  groups?: (CreateGroup & { id?: number })[]
) => {
  if (!previousEvent) {
    return false;
  }
  if (isBefore(new Date(), new Date(previousEvent.registrationPeriodStart))) {
    return false;
  }
  const previousIndex =
    previousEvent.groups?.findIndex(
      previousGroup => groups && groups[index].id === previousGroup.id
    ) || index;

  return (
    groups &&
    previousEvent.groups &&
    previousEvent.groups[previousIndex]?.maxParticipants > groups[index].maxParticipants
  );
};
/**
 * Creates a Template Event with data from an existing event.
 * @param event
 * @returns
 */
export const getTemplateEvent = (event: ResolvedEvent) => {
  const newEvent = {
    ...omitDeep(
      omitBy(event, (v, k) => v === null || v === undefined || k === 'id'),
      '__typename',
      'id',
      'eventId',
      'startDate',
      'endDate'
    ),
    registrationPeriodStart: null,
    registrationPeriodEnd: null,
    visibleFrom: null,
    startDate: null,
    endDate: null
  };
  return newEvent;
};
